import React, { useState, useEffect, useCallback } from 'react';
import { insightsAPI, reviewAPI, analyticsAPI } from '../services/api';
import { 
  Activity, AlertTriangle, TrendingUp, TrendingDown, Minus,
  Users, Brain, Target, Shield, Layers, ChevronRight,
  CheckCircle2, AlertCircle, XCircle, Sparkles, BarChart3,
  Clock, Zap, Flame, Briefcase, LineChart, ClipboardCheck,
  GitBranch, Gauge, ArrowRight, Building2, Calendar
} from 'lucide-react';
import { COGNITIVE_LANES } from '../mock';
import { DESIGN } from '../components/DesignSystem';
import { 
  DemoRoleSwitcher, 
  UNIQUE_IP_COLORS,
  CHIEF_OF_STAFF_COLORS,
  getInsightsRoleData 
} from '../components/focus';
import {
  CommandCenterHeroBanner,
  PremiumStatCard,
  OrganizationHealthRing,
  SoulFoodBridgeCard,
  CommandPrioritiesCard,
  CoordinationLoadChart,
  DecisionBacklogTrendChart,
  EnhancedHotspotRow,
  COSEffectivenessScore,
  CoordinationDemandHeatmap,
  COSTabNavigation,
  COS_COLORS
} from '../components/ChiefOfStaff/ChiefOfStaffInsights';

// ================================================
// CIRCULAR GAUGE COMPONENT
// ================================================
const CircularGauge = ({ value, max = 100, size = 120, strokeWidth = 10, color, label, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percentage = Math.min((value / max) * 100, 100);
  const offset = circumference - (percentage / 100) * circumference;
  
  // Color based on value
  const getColor = () => {
    if (color) return color;
    if (percentage >= 70) return '#10B981';
    if (percentage >= 40) return '#F59E0B';
    return '#EF4444';
  };
  
  const gaugeColor = getColor();
  
  return (
    <div className="relative inline-flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc with gradient */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={gaugeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            filter: `drop-shadow(0 0 8px ${gaugeColor}60)`,
            transition: 'stroke-dashoffset 1s ease-out'
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span 
          className="text-3xl font-black"
          style={{ color: gaugeColor }}
        >
          {Math.round(value)}
        </span>
        {max !== 100 && (
          <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>/{max}</span>
        )}
      </div>
      {label && (
        <div className="mt-3 text-center">
          <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>{label}</p>
          {sublabel && <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>{sublabel}</p>}
        </div>
      )}
    </div>
  );
};

// ================================================
// MINI ARC GAUGE
// ================================================
const MiniArcGauge = ({ value, label, color }) => {
  const percentage = Math.min(value, 100);
  
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-12 h-12">
        <svg className="w-12 h-12 transform -rotate-90">
          <circle cx="24" cy="24" r="18" fill="none" stroke="#E2E8F0" strokeWidth="4" />
          <circle 
            cx="24" cy="24" r="18" fill="none" 
            stroke={color} strokeWidth="4" strokeLinecap="round"
            strokeDasharray={`${percentage * 1.13} 113`}
            style={{ filter: `drop-shadow(0 0 4px ${color}60)` }}
          />
        </svg>
        <span 
          className="absolute inset-0 flex items-center justify-center text-xs font-bold"
          style={{ color }}
        >
          {Math.round(value)}
        </span>
      </div>
      <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{label}</span>
    </div>
  );
};

// ================================================
// PREMIUM EXECUTIVE SUMMARY BANNER
// ================================================
const ExecutiveSummaryBanner = ({ health, highlights, attentionAreas, recommendations }) => {
  return (
    <div 
      className="relative rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0D1B2A 0%, #1B3A4B 50%, #0D1B2A 100%)'
      }}
    >
      {/* Geometric pattern overlay */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `
            radial-gradient(ellipse at 10% 20%, ${DESIGN.colors.primary} 0%, transparent 40%),
            radial-gradient(ellipse at 90% 80%, #8B5CF6 0%, transparent 40%)
          `
        }}
      />
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z" fill="%23ffffff" fill-opacity="0.03" fill-rule="evenodd"/%3E%3C/svg%3E")'
        }}
      />
      
      <div className="relative p-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ 
              backgroundColor: 'rgba(0, 201, 167, 0.2)',
              boxShadow: '0 0 30px rgba(0, 201, 167, 0.3)'
            }}
          >
            <Sparkles className="w-6 h-6" style={{ color: DESIGN.colors.primary }} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Executive Intelligence Summary</h2>
            <p className="text-sm text-slate-400">AI-powered organizational insights</p>
          </div>
        </div>
        
        {/* Main health statement */}
        <div className="mb-8 p-6 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
          <p className="text-lg text-slate-200 leading-relaxed">
            Your organization is operating at <span className="font-bold text-white">{Math.round(health?.overall_score || 0)}% health</span>. 
            Execution momentum is {health?.trend === 'up' ? 'improving' : health?.trend === 'down' ? 'declining' : 'stable'}, with 
            key attention areas in team capacity management.
          </p>
        </div>
        
        {/* Three columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Highlights */}
          <div className="p-5 rounded-2xl" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-5 h-5" style={{ color: '#10B981' }} />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#10B981' }}>Highlights</h3>
            </div>
            <ul className="space-y-3">
              {(highlights || ['Strong execution velocity', 'Team alignment improving', 'Deliverables on track']).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-emerald-200">
                  <span style={{ color: '#10B981' }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Attention Areas */}
          <div className="p-5 rounded-2xl" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5" style={{ color: '#F59E0B' }} />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: '#F59E0B' }}>Attention Areas</h3>
            </div>
            <ul className="space-y-3">
              {(attentionAreas || ['2 team members at capacity', '3 phases past due date', 'Review backlog growing']).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-amber-200">
                  <span style={{ color: '#F59E0B' }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          
          {/* Recommendations */}
          <div className="p-5 rounded-2xl" style={{ backgroundColor: 'rgba(0, 201, 167, 0.1)', border: '1px solid rgba(0, 201, 167, 0.2)' }}>
            <div className="flex items-center gap-2 mb-4">
              <ArrowRight className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
              <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: DESIGN.colors.primary }}>Recommendations</h3>
            </div>
            <ul className="space-y-3">
              {(recommendations || ['Redistribute tasks from overloaded members', 'Schedule phase review meeting', 'Clear executive backlog items']).map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-teal-200">
                  <span style={{ color: DESIGN.colors.primary }}>→</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM HEALTH CARD WITH GAUGE
// ================================================
const PremiumHealthCard = ({ health }) => {
  const score = Math.round(health?.overall_score || 0);
  const getScoreColor = () => {
    if (score >= 70) return '#10B981';
    if (score >= 40) return '#F59E0B';
    return '#EF4444';
  };
  
  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`,
        boxShadow: DESIGN.shadows.lg
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>Organizational Health</h3>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Composite score across all metrics</p>
        </div>
        <div 
          className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold"
          style={{ 
            backgroundColor: `${getScoreColor()}15`,
            color: getScoreColor()
          }}
        >
          {health?.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : health?.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
          {health?.trend_change > 0 ? '+' : ''}{health?.trend_change || 0}%
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row items-center gap-8">
        {/* Main gauge */}
        <CircularGauge value={score} size={160} strokeWidth={14} />
        
        {/* Sub metrics */}
        <div className="flex-1 space-y-4">
          <MiniArcGauge value={health?.execution_score || 0} label="Execution" color="#00C9A7" />
          <MiniArcGauge value={health?.team_health_score || 0} label="Team Health" color="#3B82F6" />
          <MiniArcGauge value={health?.delivery_score || 0} label="Delivery" color="#8B5CF6" />
          <MiniArcGauge value={health?.collaboration_score || 0} label="Collaboration" color="#F59E0B" />
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM COGNITIVE LOAD CARD
// ================================================
const PremiumCognitiveLoadCard = ({ cognitive }) => {
  const getLoadColor = () => {
    const status = cognitive?.load_status;
    if (status === 'optimal') return '#10B981';
    if (status === 'elevated') return '#F59E0B';
    return '#EF4444';
  };
  
  const levelColors = {
    l4: '#8B5CF6',
    l3: '#3B82F6',
    l2: '#10B981',
    l1: '#94A3B8'
  };
  
  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`,
        boxShadow: DESIGN.shadows.lg
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>Cognitive Load</h3>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Work distribution by level</p>
        </div>
        <span 
          className="px-3 py-1.5 rounded-full text-sm font-bold capitalize"
          style={{ 
            backgroundColor: `${getLoadColor()}15`,
            color: getLoadColor(),
            boxShadow: `0 0 15px ${getLoadColor()}30`
          }}
        >
          {cognitive?.load_status || 'optimal'}
        </span>
      </div>
      
      <div className="flex flex-col md:flex-row items-center gap-8">
        {/* Main gauge */}
        <CircularGauge 
          value={cognitive?.overall_load || 0} 
          size={160} 
          strokeWidth={14}
          color={getLoadColor()}
        />
        
        {/* Level distribution */}
        <div className="flex-1 space-y-4 w-full">
          {[
            { key: 'l4', label: 'L4 Strategic', value: cognitive?.l4_strategic_load || 0, color: levelColors.l4 },
            { key: 'l3', label: 'L3 Improvement', value: cognitive?.l3_improvement_load || 0, color: levelColors.l3 },
            { key: 'l2', label: 'L2 Operational', value: cognitive?.l2_operational_load || 0, color: levelColors.l2 },
            { key: 'l1', label: 'L1 Execution', value: cognitive?.l1_execution_load || 0, color: levelColors.l1 },
          ].map(level => (
            <div key={level.key}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textSecondary }}>{level.label}</span>
                <span className="text-sm font-bold" style={{ color: level.color }}>{Math.round(level.value)}%</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
                <div 
                  className="h-full rounded-full transition-all duration-700"
                  style={{
                    width: `${level.value}%`,
                    background: `linear-gradient(90deg, ${level.color}CC 0%, ${level.color} 100%)`,
                    boxShadow: `0 0 10px ${level.color}60`
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM TAB NAVIGATION
// ================================================
const PremiumTabs = ({ tabs, activeTab, onTabChange }) => {
  return (
    <div 
      className="relative flex items-center gap-1 p-1.5 rounded-2xl"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`
            relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300
            ${activeTab === tab.id ? 'text-white' : ''}
          `}
          style={{
            color: activeTab === tab.id ? 'white' : DESIGN.colors.textSecondary,
            backgroundColor: activeTab === tab.id ? DESIGN.colors.primary : 'transparent',
            boxShadow: activeTab === tab.id ? DESIGN.shadows.glowPrimary : 'none'
          }}
        >
          <span className="flex items-center gap-2">
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
            {tab.count !== undefined && (
              <span 
                className="px-2 py-0.5 rounded-full text-xs"
                style={{ 
                  backgroundColor: activeTab === tab.id ? 'rgba(255,255,255,0.2)' : DESIGN.colors.border,
                  color: activeTab === tab.id ? 'white' : DESIGN.colors.textMuted
                }}
              >
                {tab.count}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  );
};

// ================================================
// PREMIUM RISK CARD
// ================================================
const PremiumRiskCard = ({ risk }) => {
  const severityConfigs = {
    high: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.3)', icon: XCircle, glow: true },
    medium: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', icon: AlertCircle },
    low: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.08)', border: 'rgba(59, 130, 246, 0.3)', icon: AlertTriangle }
  };
  
  const config = severityConfigs[risk.severity] || severityConfigs.low;
  const Icon = config.icon;
  
  return (
    <div 
      className={`relative p-5 rounded-2xl border-l-4 transition-all hover:shadow-lg ${config.glow ? 'animate-pulse-slow' : ''}`}
      style={{ 
        backgroundColor: config.bg, 
        borderLeftColor: config.color,
        boxShadow: config.glow ? `0 0 20px ${config.color}30` : 'none'
      }}
    >
      <div className="flex items-start gap-4">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${config.color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color: config.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h4 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>{risk.title}</h4>
            <span 
              className="px-3 py-1 rounded-full text-xs font-bold uppercase flex-shrink-0"
              style={{ backgroundColor: config.color, color: 'white' }}
            >
              {risk.severity}
            </span>
          </div>
          <p className="text-sm mb-3" style={{ color: DESIGN.colors.textSecondary }}>{risk.description}</p>
          <button 
            className="flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
            style={{ color: config.color }}
          >
            {risk.recommended_action}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// HOTSPOT CARD
// ================================================
const HotspotCard = ({ person }) => {
  const levelConfigs = {
    critical: { color: '#EF4444', bg: 'rgba(239, 68, 68, 0.08)' },
    high: { color: '#F97316', bg: 'rgba(249, 115, 22, 0.08)' },
    elevated: { color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.08)' },
    optimal: { color: '#10B981', bg: 'rgba(16, 185, 129, 0.08)' }
  };
  
  const config = levelConfigs[person.level] || levelConfigs.optimal;
  
  return (
    <div 
      className="p-5 rounded-2xl border transition-all hover:shadow-lg cursor-pointer"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        borderColor: config.color,
        borderWidth: '2px'
      }}
    >
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
          style={{ 
            background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
            color: 'white'
          }}
        >
          {person.avatar}
        </div>
        <div className="flex-1">
          <h4 className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{person.name}</h4>
          <p className="text-sm" style={{ color: DESIGN.colors.textMuted }}>{person.role}</p>
        </div>
        <CircularGauge value={person.score} size={60} strokeWidth={6} color={config.color} />
      </div>
      
      <div className="space-y-3">
        {[
          { label: 'Active Tasks', value: person.active_tasks, max: 12 },
          { label: 'Lane Switching', value: person.lane_switches, max: 10 },
          { label: 'Task Complexity', value: person.complexity, max: 100 }
        ].map(metric => (
          <div key={metric.label}>
            <div className="flex justify-between text-xs mb-1">
              <span style={{ color: DESIGN.colors.textMuted }}>{metric.label}</span>
              <span style={{ color: DESIGN.colors.textSecondary }}>{metric.value}{metric.max !== 100 ? `/${metric.max}` : '%'}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: '#E2E8F0' }}>
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(metric.value / metric.max) * 100}%`,
                  background: `linear-gradient(90deg, ${config.color}CC 0%, ${config.color} 100%)`
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// METRIC TERMINAL CARD
// ================================================
const MetricTerminalCard = ({ metric }) => {
  const trendColor = metric.trend === 'up' ? '#10B981' : metric.trend === 'down' ? '#EF4444' : DESIGN.colors.textMuted;
  
  return (
    <div 
      className="p-4 rounded-xl min-w-[180px] flex-shrink-0"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <p className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: DESIGN.colors.textMuted }}>
        {metric.name}
      </p>
      <div className="flex items-end justify-between">
        <span className="text-2xl font-black" style={{ color: DESIGN.colors.textPrimary }}>
          {metric.unit === 'percentage' ? `${Math.round(metric.value)}%` : metric.value}
        </span>
        <div className="flex items-center gap-1 text-sm font-semibold" style={{ color: trendColor }}>
          {metric.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : metric.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
          {metric.trend_value > 0 ? '+' : ''}{metric.trend_value}
        </div>
      </div>
    </div>
  );
};

// ================================================
// LANE PERFORMANCE CARD
// ================================================
const LanePerformanceCard = ({ lane }) => {
  const statusConfigs = {
    full: { label: 'Full Coverage', color: '#10B981' },
    partial: { label: 'Partial', color: '#F59E0B' },
    gap: { label: 'Gap', color: '#EF4444' }
  };
  
  const config = statusConfigs[lane.coverage_status] || statusConfigs.gap;
  
  return (
    <div 
      className="p-5 rounded-2xl border-l-4"
      style={{ 
        backgroundColor: `${lane.color}08`,
        borderLeftColor: lane.color
      }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: lane.color, boxShadow: `0 0 10px ${lane.color}60` }}
          />
          <h4 className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{lane.lane_name}</h4>
        </div>
        <span 
          className="px-3 py-1 rounded-full text-xs font-bold"
          style={{ backgroundColor: `${config.color}15`, color: config.color }}
        >
          {config.label}
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>{lane.active_tasks}</p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Active</p>
        </div>
        <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>{lane.completed_tasks}</p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Done</p>
        </div>
        <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-2xl font-bold" style={{ color: lane.on_time_rate >= 0.8 ? '#10B981' : '#F59E0B' }}>
            {Math.round(lane.on_time_rate * 100)}%
          </p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>On-Time</p>
        </div>
      </div>
    </div>
  );
};

// ================================================
// PERSONAL OVERVIEW CARD (Team Member & Ops)
// ================================================
const PersonalOverviewCard = ({ data, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  
  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <Target className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
          {isUniqueIP ? 'My Operations Overview' : 'My Performance Overview'}
        </h3>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-3xl font-black" style={{ color: accentColor }}>
            {data.performanceScore || data.operationsScore}%
          </p>
          <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
            {isUniqueIP ? 'Ops Score' : 'Performance'}
          </p>
        </div>
        <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-3xl font-black" style={{ color: '#10B981' }}>
            {data.completionRate || data.tasksOnTime}%
          </p>
          <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
            On-Time Rate
          </p>
        </div>
        {isUniqueIP ? (
          <>
            <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-3xl font-black" style={{ color: accentColor }}>
                {data.eventsSupported}
              </p>
              <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                Events Supported
              </p>
            </div>
            <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-3xl font-black" style={{ color: '#10B981' }}>
                {data.issuesResolved}
              </p>
              <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                Issues Resolved
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-3xl font-black" style={{ color: data.loadTrend === 'stable' ? '#3B82F6' : data.loadTrend === 'up' ? '#F59E0B' : '#10B981' }}>
                {data.loadTrend === 'stable' ? '→' : data.loadTrend === 'up' ? '↑' : '↓'}
              </p>
              <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                Load Trend
              </p>
            </div>
            <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-3xl font-black" style={{ color: accentColor }}>
                {data.laneAlignment}%
              </p>
              <p className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                Lane Alignment
              </p>
            </div>
          </>
        )}
      </div>

      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: `${accentColor}08` }}
      >
        <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
          {data.summaryStatement}
        </p>
      </div>

      {/* Mini Load Trend Chart */}
      {data.loadTrendChart && (
        <div className="mt-4">
          <p className="text-xs font-medium mb-2" style={{ color: DESIGN.colors.textMuted }}>
            8-Week Load Trend
          </p>
          <div className="flex items-end gap-1 h-12">
            {data.loadTrendChart.map((value, i) => (
              <div 
                key={i}
                className="flex-1 rounded-t"
                style={{ 
                  height: `${(value / 100) * 100}%`,
                  backgroundColor: value > 75 ? '#EF4444' : value > 60 ? '#F59E0B' : accentColor
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ================================================
// MY COGNITIVE PATTERNS (replaces Hotspots)
// ================================================
const MyCognitivePatterns = ({ data, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  const title = isUniqueIP ? 'My Operational Patterns' : 'My Cognitive Patterns';
  
  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
        >
          <Brain className="w-5 h-5" style={{ color: '#8B5CF6' }} />
        </div>
        <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
          {title}
        </h3>
      </div>

      {/* Weekly Pattern */}
      <div className="mb-6">
        <p className="text-xs font-medium mb-3" style={{ color: DESIGN.colors.textMuted }}>
          Weekly Load Pattern
        </p>
        <div className="flex gap-2">
          {data.weeklyPattern.map((day) => (
            <div key={day.day} className="flex-1 text-center">
              <div 
                className="h-16 rounded-lg mb-2 flex items-end justify-center relative overflow-hidden"
                style={{ backgroundColor: DESIGN.colors.background }}
              >
                <div 
                  className="w-full rounded-t transition-all"
                  style={{ 
                    height: `${day.load}%`,
                    backgroundColor: day.load > 75 ? '#EF4444' : day.load > 60 ? '#F59E0B' : '#10B981'
                  }}
                />
              </div>
              <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
                {day.day}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Peak Days */}
      <div className="mb-4">
        <p className="text-xs font-medium mb-2" style={{ color: DESIGN.colors.textMuted }}>
          {isUniqueIP ? 'High Complexity Days' : 'Peak Load Days'}
        </p>
        <div className="flex gap-2">
          {(data.peakDays || data.complexDays).map((day) => (
            <span 
              key={day}
              className="px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}
            >
              {day}
            </span>
          ))}
        </div>
        <p className="text-xs mt-2" style={{ color: DESIGN.colors.textMuted }}>
          {data.peakReason || data.complexReason}
        </p>
      </div>

      {/* High Effort Tasks */}
      <div className="mb-4">
        <p className="text-xs font-medium mb-2" style={{ color: DESIGN.colors.textMuted }}>
          {isUniqueIP ? 'High Coordination Tasks' : 'Cognitive Strain Sources'}
        </p>
        <div className="flex flex-wrap gap-2">
          {(data.strainTasks || data.effortTasks).map((task, i) => (
            <span 
              key={i}
              className="px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ backgroundColor: DESIGN.colors.background, color: DESIGN.colors.textSecondary }}
            >
              {task}
            </span>
          ))}
        </div>
      </div>

      {/* Recommendation */}
      <div 
        className="p-4 rounded-xl"
        style={{ backgroundColor: `${accentColor}08`, borderLeft: `3px solid ${accentColor}` }}
      >
        <p className="text-xs font-bold mb-1" style={{ color: accentColor }}>
          Recommendation
        </p>
        <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
          {data.recommendation}
        </p>
      </div>
    </div>
  );
};

// ================================================
// MY PROJECTS (Personal contribution view)
// ================================================
const MyProjectsView = ({ projects, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  
  return (
    <div className="space-y-4">
      {projects.map((project) => (
        <div 
          key={project.id}
          className="p-5 rounded-2xl"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`
          }}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>{project.name}</h4>
              <p className="text-xs mt-0.5" style={{ color: accentColor }}>{project.role}</p>
            </div>
            <div 
              className="px-3 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: `${project.projectHealth >= 75 ? '#10B981' : '#F59E0B'}15`, color: project.projectHealth >= 75 ? '#10B981' : '#F59E0B' }}
            >
              {project.projectHealth}% Healthy
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                {project.myTasks}/{project.totalTasks}
              </p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>My Tasks</p>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-xl font-bold" style={{ color: project.myOnTimeRate >= 85 ? '#10B981' : '#F59E0B' }}>
                {project.myOnTimeRate}%
              </p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>My On-Time</p>
            </div>
            <div className="text-center p-3 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
              <p className="text-xl font-bold" style={{ color: accentColor }}>
                {Math.round((project.myTasks / project.totalTasks) * 100)}%
              </p>
              <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Contribution</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ================================================
// PERSONAL TRENDS (8-week charts)
// ================================================
const PersonalTrends = ({ trends, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary;
  
  return (
    <div className="space-y-6">
      {Object.entries(trends).map(([key, trend]) => (
        <div 
          key={key}
          className="p-5 rounded-2xl"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`
          }}
        >
          <h4 className="font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
            {trend.label}
          </h4>
          
          {/* Bar Chart */}
          <div className="flex items-end gap-2 h-24 mb-3">
            {trend.data.map((value, i) => {
              const max = Math.max(...trend.data);
              const height = (value / max) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full rounded-t transition-all"
                    style={{ 
                      height: `${height}%`,
                      backgroundColor: accentColor,
                      opacity: 0.6 + (i / trend.data.length) * 0.4
                    }}
                  />
                </div>
              );
            })}
          </div>
          
          <div className="flex justify-between text-xs" style={{ color: DESIGN.colors.textMuted }}>
            <span>Week 1</span>
            <span>Week 8</span>
          </div>
          
          {/* AI Insight */}
          <div 
            className="mt-4 p-3 rounded-xl text-sm"
            style={{ backgroundColor: `${accentColor}08` }}
          >
            <Sparkles className="w-4 h-4 inline mr-2" style={{ color: accentColor }} />
            <span style={{ color: DESIGN.colors.textSecondary }}>{trend.insight}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ================================================
// MY LANE VIEW (Single lane - personal)
// ================================================
const MyLaneView = ({ lane, isUniqueIP }) => {
  const accentColor = isUniqueIP ? UNIQUE_IP_COLORS.primary : lane.color || DESIGN.colors.primary;
  
  return (
    <div 
      className="rounded-2xl p-6"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        border: `1px solid ${DESIGN.colors.border}`,
        borderLeft: `4px solid ${accentColor}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${accentColor}15` }}
        >
          <GitBranch className="w-5 h-5" style={{ color: accentColor }} />
        </div>
        <div>
          <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
            {lane.name} Lane
          </h3>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Your assigned lane</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-3xl font-black" style={{ color: accentColor }}>{lane.laneHealth}%</p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>Lane Health</p>
        </div>
        <div className="text-center p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
          <p className="text-3xl font-black" style={{ color: DESIGN.colors.textPrimary }}>{lane.myContribution}</p>
          <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>My Contribution</p>
        </div>
      </div>

      {/* Comparison to average */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>My On-Time Rate</span>
          <span className="font-bold" style={{ color: '#10B981' }}>{lane.myOnTimeInLane}%</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm" style={{ color: DESIGN.colors.textMuted }}>Lane Average</span>
          <span className="font-medium" style={{ color: DESIGN.colors.textSecondary }}>{lane.laneAvgOnTime || lane.laneAvgReliability}%</span>
        </div>
      </div>

      {/* Percentile or supportive message */}
      {lane.contributionPercentile && (
        <div 
          className="mt-4 p-3 rounded-xl text-sm"
          style={{ backgroundColor: `${accentColor}08` }}
        >
          <span style={{ color: DESIGN.colors.textSecondary }}>
            You're in the <strong style={{ color: accentColor }}>top {100 - lane.contributionPercentile}%</strong> of contributors in this lane
          </span>
        </div>
      )}

      <div 
        className="mt-4 p-4 rounded-xl"
        style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)' }}
      >
        <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
          {lane.supportiveMessage}
        </p>
      </div>
    </div>
  );
};

// ================================================
// MAIN INSIGHTS PAGE
// ================================================
const Insights = () => {
  const [activeTab, setActiveTab] = useState('risks');
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Demo role switcher state
  const [activeRole, setActiveRole] = useState('ceo');
  const [demoData, setDemoData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [insightsRes] = await Promise.all([
          insightsAPI.getDashboard()
        ]);
        setInsights(insightsRes.data);
      } catch (error) {
        console.error('Error fetching insights:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Role change handler
  const handleRoleChange = useCallback((newRole) => {
    setActiveRole(newRole);
    const roleData = getInsightsRoleData(newRole);
    setDemoData(roleData);
  }, []);

  // Get display data - use demo data if available, otherwise API data
  const displayInsights = demoData || insights;
  const isUniqueIP = demoData?.isUniqueIP;
  const isChiefOfStaff = demoData?.isChiefOfStaff;
  const isPersonalView = demoData?.isPersonalView;
  const accentColor = isChiefOfStaff 
    ? CHIEF_OF_STAFF_COLORS.primary 
    : isUniqueIP 
      ? UNIQUE_IP_COLORS.primary 
      : DESIGN.colors.primary;

  // Personal tabs for Team Member / Unique IP Ops
  const personalTabs = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'patterns', label: isUniqueIP ? 'Patterns' : 'Patterns', icon: Brain },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'trends', label: 'Trends', icon: LineChart },
    { id: 'lanes', label: 'My Lane', icon: GitBranch }
  ];

  // Chief of Staff tabs - unique Coordination Intelligence tab
  const chiefOfStaffTabs = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'hotspots', label: 'Hotspots', icon: Flame, count: (displayInsights?.hotspots || []).length },
    { id: 'projects', label: 'Projects', icon: Briefcase },
    { id: 'trends', label: 'Trends', icon: LineChart },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'lanes', label: 'Lanes', icon: GitBranch },
    { id: 'reviews', label: 'Reviews', icon: ClipboardCheck },
    { id: 'coordination', label: 'Coordination Intelligence', icon: Shield }
  ];

  const tabs = [
    { id: 'risks', label: 'Risks', icon: Shield, count: (displayInsights?.risks || []).length },
    { id: 'hotspots', label: 'Hotspots', icon: Flame, count: (displayInsights?.hotspots || []).length || 5 },
    { id: 'projects', label: 'Projects', icon: Target },
    { id: 'trends', label: 'Trends', icon: LineChart },
    { id: 'metrics', label: 'Metrics', icon: BarChart3 },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'lanes', label: 'Lanes', icon: GitBranch },
    { id: 'reviews', label: 'Reviews', icon: ClipboardCheck }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: `${DESIGN.colors.primary}30`, borderTopColor: DESIGN.colors.primary }} />
          </div>
          <p className="text-sm font-medium" style={{ color: DESIGN.colors.textMuted }}>Loading insights...</p>
        </div>
      </div>
    );
  }

  // CHIEF OF STAFF VIEW - Premium War Room Command Center
  if (isChiefOfStaff && demoData?.chiefOfStaffDashboard) {
    // Sparkline data for stat cards
    const sparklineData = {
      followUp: [15, 14, 16, 13, 14, 12, 11, 12],
      decisions: [14, 15, 16, 17, 16, 18, 17, 18],
      escalations: [8, 7, 6, 5, 6, 5, 4, 4],
      timeProtected: [65, 68, 70, 72, 74, 75, 77, 78]
    };

    // Updated tabs with exclusive marker for Coordination Intelligence
    const premiumTabs = [
      { id: 'overview', label: 'Overview', icon: Target },
      { id: 'hotspots', label: 'Hotspots', icon: Flame, count: (demoData?.hotspots || []).length },
      { id: 'coordination', label: 'Coordination Intelligence', icon: Shield, exclusive: true },
      { id: 'projects', label: 'Projects', icon: Briefcase },
      { id: 'trends', label: 'Trends', icon: LineChart },
      { id: 'team', label: 'Team', icon: Users },
      { id: 'lanes', label: 'Lanes', icon: GitBranch },
      { id: 'reviews', label: 'Reviews', icon: ClipboardCheck }
    ];
    
    return (
      <div className="space-y-8" data-testid="insights-page" data-testid-cos="chief-of-staff-insights">
        <DemoRoleSwitcher activeRole={activeRole} onRoleChange={handleRoleChange} />
        
        {/* PREMIUM HERO BANNER - War Room Command Center */}
        <CommandCenterHeroBanner />
        
        {/* ELEVATED STAT CARDS WITH SPARKLINES */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <PremiumStatCard 
            label="Items Requiring Follow-Up"
            value={demoData.chiefOfStaffDashboard.itemsRequiringFollowUp.value}
            trend="up"
            trendValue={demoData.chiefOfStaffDashboard.itemsRequiringFollowUp.trend}
            sparklineData={sparklineData.followUp}
            trendDirection="down-is-good"
          />
          <PremiumStatCard 
            label="Decisions Made This Week"
            value={demoData.chiefOfStaffDashboard.decisionsMadeThisWeek.value}
            trend="up"
            trendValue={demoData.chiefOfStaffDashboard.decisionsMadeThisWeek.trend}
            sparklineData={sparklineData.decisions}
          />
          <PremiumStatCard 
            label="Cross-Dept Escalations"
            value={demoData.chiefOfStaffDashboard.crossDeptEscalations.value}
            trend="down"
            trendValue={demoData.chiefOfStaffDashboard.crossDeptEscalations.trend}
            sparklineData={sparklineData.escalations}
            trendDirection="down-is-good"
          />
          <PremiumStatCard 
            label="Executive Time Protected"
            value={demoData.chiefOfStaffDashboard.executiveTimeProtected.value}
            unit="%"
            trend="up"
            trendValue={5}
            sparklineData={sparklineData.timeProtected}
          />
        </div>

        {/* PREMIUM TAB NAVIGATION */}
        <COSTabNavigation 
          tabs={premiumTabs} 
          activeTab={activeTab} 
          onTabChange={setActiveTab} 
        />

        {/* Tab Content */}
        <div className="space-y-6">
          {/* OVERVIEW TAB - Complete Redesign with 3-Column Layout */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* 3-Column Top Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Organization Health Ring */}
                <OrganizationHealthRing 
                  overall={demoData.health.overall_score}
                  execution={demoData.health.execution_score}
                  teamHealth={demoData.health.team_health_score}
                  delivery={demoData.health.delivery_score}
                />
                
                {/* Center: Soul Food Bridge */}
                <SoulFoodBridgeCard 
                  mainCompanyHealth={demoData.health.overall_score}
                  soulFoodHealth={demoData.soulFoodHealth?.healthScore || 82}
                />
                
                {/* Right: Command Priorities */}
                <CommandPrioritiesCard />
              </div>
              
              {/* Two Full-Width Cards Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Coordination Load by Department */}
                <CoordinationLoadChart 
                  data={demoData.coordinationIntelligence?.escalationSources}
                />
                
                {/* Decision Backlog Trend */}
                <DecisionBacklogTrendChart 
                  data={demoData.coordinationIntelligence?.decisionBacklogTrend}
                />
              </div>
            </div>
          )}
          
          {/* HOTSPOTS TAB - Enhanced with AI Insights */}
          {activeTab === 'hotspots' && (
            <div 
              className="p-6 rounded-2xl"
              style={{ 
                backgroundColor: DESIGN.colors.card, 
                boxShadow: DESIGN.shadows.lg,
                border: `1px solid ${COS_COLORS.cardBorder}`
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                  Cognitive Load Hotspots
                </h3>
                <button 
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                  style={{ 
                    backgroundColor: COS_COLORS.primarySubtle, 
                    color: COS_COLORS.primary 
                  }}
                >
                  Filter: Priority for Executive
                </button>
              </div>
              <div className="space-y-4">
                {demoData.hotspots?.map((hotspot, idx) => (
                  <EnhancedHotspotRow 
                    key={idx}
                    person={hotspot}
                    isExecPriority={hotspot.priorityForExecutive}
                  />
                ))}
              </div>
            </div>
          )}
          
          {/* COORDINATION INTELLIGENCE TAB - Premium Exclusive */}
          {activeTab === 'coordination' && demoData.coordinationIntelligence && (
            <div className="space-y-6">
              {/* CoS Effectiveness Score - Premium Top Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <COSEffectivenessScore score={92} percentile={8} />
                
                <div 
                  className="lg:col-span-2 p-6 rounded-xl"
                  style={{ 
                    backgroundColor: DESIGN.colors.card, 
                    boxShadow: DESIGN.shadows.md,
                    border: `1px solid ${COS_COLORS.cardBorder}`
                  }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5" style={{ color: COS_COLORS.primary }} />
                    <h3 className="text-base font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      Coordination Intelligence
                    </h3>
                    <span 
                      className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ backgroundColor: COS_COLORS.primary, color: 'white' }}
                    >
                      Exclusive
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                      <p className="text-xs font-medium mb-1" style={{ color: DESIGN.colors.textMuted }}>
                        Avg Executive Response Time
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-black" style={{ color: COS_COLORS.primary }}>
                          {demoData.coordinationIntelligence.avgExecutiveResponseTime.value}
                        </span>
                        <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                          {demoData.coordinationIntelligence.avgExecutiveResponseTime.unit}
                        </span>
                        <span className="text-xs flex items-center gap-1 text-green-500 ml-auto">
                          <TrendingDown className="w-3 h-3" /> Improving
                        </span>
                      </div>
                    </div>
                    <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                      <p className="text-xs font-medium mb-1" style={{ color: DESIGN.colors.textMuted }}>
                        Executive Time Protected
                      </p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-3xl font-black" style={{ color: COS_COLORS.primary }}>
                          78%
                        </span>
                        <span className="text-xs flex items-center gap-1 text-green-500 ml-auto">
                          <TrendingUp className="w-3 h-3" /> +5%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Coordination Demand Heatmap */}
              <CoordinationDemandHeatmap data={demoData.coordinationIntelligence.calendarHeatmap} />
              
              {/* Escalation Sources & Decision Backlog */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CoordinationLoadChart 
                  data={demoData.coordinationIntelligence.escalationSources}
                />
                <DecisionBacklogTrendChart 
                  data={demoData.coordinationIntelligence.decisionBacklogTrend}
                />
              </div>
            </div>
          )}
          
          {/* PROJECTS TAB with Executive Visibility */}
          {activeTab === 'projects' && (
            <div 
              className="p-6 rounded-2xl"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
                All Projects - Executive Visibility Status
              </h3>
              <div className="space-y-3">
                {demoData.allProjects?.map((project) => (
                  <div 
                    key={project.id}
                    className="flex items-center justify-between p-4 rounded-xl"
                    style={{ 
                      backgroundColor: DESIGN.colors.background,
                      borderLeft: project.entity === 'soulfood' 
                        ? `4px solid ${UNIQUE_IP_COLORS.primary}` 
                        : `4px solid #1E3A5F`
                    }}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium" style={{ color: DESIGN.colors.textPrimary }}>{project.name}</p>
                        <span 
                          className="px-2 py-0.5 rounded-full text-xs"
                          style={{ 
                            backgroundColor: project.entity === 'soulfood' ? UNIQUE_IP_COLORS.primarySubtle : '#1E3A5F20',
                            color: project.entity === 'soulfood' ? UNIQUE_IP_COLORS.primary : '#1E3A5F'
                          }}
                        >
                          {project.entity === 'soulfood' ? 'Soul Food' : 'Company'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex-1 h-2 rounded-full overflow-hidden max-w-48" style={{ backgroundColor: DESIGN.colors.border }}>
                          <div 
                            className="h-full rounded-full"
                            style={{ 
                              width: `${project.progress}%`,
                              backgroundColor: project.status === 'critical' ? '#EF4444' : project.status === 'attention' ? '#F59E0B' : '#10B981'
                            }}
                          />
                        </div>
                        <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>{project.progress}%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {project.executiveBriefed ? (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                          <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                            Briefed {project.lastBriefed}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-500" />
                          <span className="text-sm text-amber-600">
                            {project.overdue ? 'Overdue' : 'Not Briefed'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Reviews Tab - Detailed Pipeline */}
          {activeTab === 'reviews' && (
            <div 
              className="p-6 rounded-2xl"
              style={{ 
                backgroundColor: DESIGN.colors.card, 
                boxShadow: DESIGN.shadows.lg,
                border: `2px solid ${CHIEF_OF_STAFF_COLORS.primary}30`
              }}
            >
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: DESIGN.colors.textPrimary }}>
                <ClipboardCheck className="w-5 h-5" style={{ color: CHIEF_OF_STAFF_COLORS.primary }} />
                Executive Review Pipeline
              </h3>
              <div className="space-y-3">
                {demoData.executiveReviewPipeline?.map((item) => (
                  <div 
                    key={item.id}
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: DESIGN.colors.background }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium" style={{ color: DESIGN.colors.textPrimary }}>{item.name}</p>
                      <div className="flex items-center gap-2">
                        <span 
                          className="px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ 
                            backgroundColor: item.sla_status === 'at_risk' ? '#FEE2E2' 
                              : item.sla_status === 'warning' ? '#FEF3C7' 
                              : '#D1FAE5',
                            color: item.sla_status === 'at_risk' ? '#EF4444' 
                              : item.sla_status === 'warning' ? '#F59E0B' 
                              : '#10B981'
                          }}
                        >
                          {item.sla_countdown}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                        <span>{item.type}</span>
                        <span>•</span>
                        <span>By {item.submitted_by}</span>
                        <span>•</span>
                        <span>{item.content_summary}</span>
                      </div>
                      <div className="flex gap-2">
                        <button 
                          className="px-3 py-1 rounded-lg text-xs font-medium"
                          style={{ backgroundColor: DESIGN.colors.border, color: DESIGN.colors.textSecondary }}
                        >
                          Request Clarification
                        </button>
                        <button 
                          className="px-3 py-1 rounded-lg text-xs font-medium"
                          style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primary, color: 'white' }}
                        >
                          Escalate
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Team Tab with Needs Attention Filter */}
          {activeTab === 'team' && (
            <div 
              className="p-6 rounded-2xl"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                  All Team Members
                </h3>
                <button 
                  className="px-3 py-1.5 rounded-lg text-sm font-medium"
                  style={{ 
                    backgroundColor: CHIEF_OF_STAFF_COLORS.primarySubtle, 
                    color: CHIEF_OF_STAFF_COLORS.primary 
                  }}
                >
                  Filter: Needs CoS Attention
                </button>
              </div>
              <div className="space-y-2">
                {demoData.allTeamMembers?.map((member) => (
                  <div 
                    key={member.id}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ 
                      backgroundColor: DESIGN.colors.background,
                      borderLeft: member.needsAttention ? `4px solid ${CHIEF_OF_STAFF_COLORS.primary}` : 'none'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs"
                        style={{ backgroundColor: DESIGN.colors.primary }}
                      >
                        {member.avatar}
                      </div>
                      <div>
                        <p className="font-medium text-sm" style={{ color: DESIGN.colors.textPrimary }}>{member.name}</p>
                        <p className="text-xs" style={{ color: DESIGN.colors.textSecondary }}>{member.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span 
                        style={{ 
                          color: member.burnoutRisk > 75 ? '#EF4444' 
                            : member.burnoutRisk > 60 ? '#F59E0B' 
                            : '#10B981' 
                        }}
                      >
                        Burnout: {member.burnoutRisk}%
                      </span>
                      {member.overdueItems > 0 && (
                        <span className="text-amber-600">
                          {member.overdueItems} overdue
                        </span>
                      )}
                      {member.flaggedForFollowup && (
                        <span 
                          className="px-2 py-0.5 rounded-full text-xs"
                          style={{ backgroundColor: CHIEF_OF_STAFF_COLORS.primarySubtle, color: CHIEF_OF_STAFF_COLORS.primary }}
                        >
                          Follow-up
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Lanes Tab */}
          {activeTab === 'lanes' && (
            <div 
              className="p-6 rounded-2xl"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
                All Cognitive Lanes
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {demoData.lane_performance?.map((lane, idx) => (
                  <div 
                    key={idx}
                    className="p-4 rounded-xl text-center"
                    style={{ backgroundColor: DESIGN.colors.background }}
                  >
                    <div 
                      className="w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center"
                      style={{ backgroundColor: lane.color + '20' }}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lane.color }} />
                    </div>
                    <p className="text-sm font-medium truncate" style={{ color: DESIGN.colors.textPrimary }}>
                      {lane.name}
                    </p>
                    <p 
                      className="text-lg font-bold mt-1"
                      style={{ color: lane.health_score > 80 ? '#10B981' : lane.health_score > 60 ? '#F59E0B' : '#EF4444' }}
                    >
                      {lane.health_score}
                    </p>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      {lane.velocity_trend === 'up' 
                        ? <TrendingUp className="w-3 h-3 text-green-500" />
                        : <TrendingDown className="w-3 h-3 text-red-500" />
                      }
                      <span className="text-xs" style={{ color: DESIGN.colors.textTertiary }}>
                        {Math.round(lane.on_time_percentage * 100)}% on-time
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Trends Tab */}
          {activeTab === 'trends' && demoData.coordinationImpactTrends && (
            <div 
              className="p-6 rounded-2xl"
              style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
            >
              <h3 className="text-lg font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
                Coordination Impact Trends
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                    {demoData.coordinationImpactTrends.executiveReviewTurnaround.label}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      {demoData.coordinationImpactTrends.executiveReviewTurnaround.current}h
                    </span>
                    <span className="text-sm text-green-500 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      from {demoData.coordinationImpactTrends.executiveReviewTurnaround.previous}h
                    </span>
                  </div>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                    {demoData.coordinationImpactTrends.crossDeptEscalations.label}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      {demoData.coordinationImpactTrends.crossDeptEscalations.current}
                    </span>
                    <span className="text-sm text-green-500 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      from {demoData.coordinationImpactTrends.crossDeptEscalations.previous}
                    </span>
                  </div>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                    {demoData.coordinationImpactTrends.decisionBacklog.label}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      {demoData.coordinationImpactTrends.decisionBacklog.current}
                    </span>
                    <span className="text-sm text-green-500 flex items-center gap-1">
                      <TrendingDown className="w-3 h-3" />
                      from {demoData.coordinationImpactTrends.decisionBacklog.previous}
                    </span>
                  </div>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: DESIGN.colors.background }}>
                  <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                    {demoData.coordinationImpactTrends.meetingsFiltered.label}
                  </p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="text-2xl font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                      {demoData.coordinationImpactTrends.meetingsFiltered.current}
                    </span>
                    <span className="text-sm text-green-500 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      +{demoData.coordinationImpactTrends.meetingsFiltered.current - demoData.coordinationImpactTrends.meetingsFiltered.previous}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Use demo data risks/hotspots/metrics if available, otherwise use defaults
  const mockRisks = displayInsights?.risks || insights?.risks || [
    { title: 'Team capacity approaching critical threshold', severity: 'high', description: '3 team members are at 95%+ utilization', recommended_action: 'Redistribute workload immediately' },
    { title: 'Phase delivery delays detected', severity: 'medium', description: '5 phases are past their due dates', recommended_action: 'Review and update timelines' },
    { title: 'Lane coverage gap in Operations', severity: 'medium', description: 'No L4 coverage for operations lane', recommended_action: 'Assign strategic owner' }
  ];
  
  const mockHotspots = displayInsights?.hotspots || [
    { name: 'Marcus Rivera', avatar: 'MR', role: 'Engineering Lead', score: 92, level: 'critical', active_tasks: 11, lane_switches: 8, complexity: 85 },
    { name: 'James O\'Brien', avatar: 'JO', role: 'VP Sales', score: 88, level: 'high', active_tasks: 10, lane_switches: 6, complexity: 78 },
    { name: 'Michael Zhang', avatar: 'MZ', role: 'Principal Architect', score: 85, level: 'elevated', active_tasks: 9, lane_switches: 5, complexity: 82 },
    { name: 'Rachel Wong', avatar: 'RW', role: 'Growth Lead', score: 78, level: 'elevated', active_tasks: 8, lane_switches: 4, complexity: 70 },
    { name: 'Sarah Chen', avatar: 'SC', role: 'Head of Product', score: 45, level: 'optimal', active_tasks: 4, lane_switches: 2, complexity: 55 }
  ];
  
  const mockMetrics = displayInsights?.key_metrics || insights?.key_metrics || [
    { name: 'Velocity', value: 124, unit: 'number', trend: 'up', trend_value: 12 },
    { name: 'On-Time Rate', value: 87, unit: 'percentage', trend: 'up', trend_value: 3 },
    { name: 'Team Utilization', value: 78, unit: 'percentage', trend: 'down', trend_value: -2 },
    { name: 'Phase Completion', value: 92, unit: 'percentage', trend: 'up', trend_value: 5 },
    { name: 'Risk Score', value: 23, unit: 'number', trend: 'down', trend_value: -8 }
  ];

  const mockLanes = COGNITIVE_LANES.slice(0, 6).map(lane => ({
    lane_name: lane.name,
    color: lane.color,
    coverage_status: Math.random() > 0.3 ? 'full' : Math.random() > 0.5 ? 'partial' : 'gap',
    active_tasks: Math.floor(Math.random() * 15) + 5,
    completed_tasks: Math.floor(Math.random() * 30) + 10,
    on_time_rate: 0.7 + Math.random() * 0.25
  }));

  // PERSONAL VIEW for Team Member and Unique IP Operations
  if (isPersonalView && demoData?.myPerformanceOverview) {
    return (
      <div className="space-y-8" data-testid="insights-page">
        {/* Demo Role Switcher */}
        <DemoRoleSwitcher 
          activeRole={activeRole} 
          onRoleChange={handleRoleChange}
        />
        
        {/* Scope Label */}
        {demoData?.scope_label && (
          <div className="flex items-center gap-3">
            <span 
              className="text-sm font-bold px-4 py-2 rounded-xl"
              style={{ 
                backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : DESIGN.colors.primarySubtle,
                color: accentColor
              }}
            >
              {demoData.scope_label}
            </span>
          </div>
        )}

        {/* Personal Health Score Hero */}
        <div 
          className="rounded-2xl p-6"
          style={{ 
            background: isUniqueIP 
              ? 'linear-gradient(135deg, #451a03 0%, #78350f 50%, #451a03 100%)'
              : 'linear-gradient(135deg, #0D1B2A 0%, #1B263B 50%, #0D1B2A 100%)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                {demoData.health.label || 'My Performance Score'}
              </p>
              <div className="flex items-center gap-4">
                <span className="text-5xl font-black text-white">
                  {demoData.health.overall_score}%
                </span>
                <div className="flex items-center gap-1">
                  {demoData.health.trend === 'up' ? (
                    <TrendingUp className="w-5 h-5" style={{ color: '#10B981' }} />
                  ) : demoData.health.trend === 'down' ? (
                    <TrendingDown className="w-5 h-5" style={{ color: '#EF4444' }} />
                  ) : (
                    <Minus className="w-5 h-5" style={{ color: '#94A3B8' }} />
                  )}
                  <span 
                    className="text-sm font-bold"
                    style={{ color: demoData.health.trend === 'up' ? '#10B981' : demoData.health.trend === 'down' ? '#EF4444' : '#94A3B8' }}
                  >
                    {demoData.health.trend_change > 0 ? '+' : ''}{demoData.health.trend_change}%
                  </span>
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Updated just now
            </div>
          </div>
        </div>
        
        {/* Personal Overview */}
        <PersonalOverviewCard data={demoData.myPerformanceOverview} isUniqueIP={isUniqueIP} />

        {/* Personal Tab Navigation */}
        <div className="overflow-x-auto pb-2">
          <PremiumTabs tabs={personalTabs} activeTab={activeTab} onTabChange={setActiveTab} />
        </div>
        
        {/* Tab Content */}
        <div 
          className="rounded-2xl p-6"
          style={{ 
            backgroundColor: DESIGN.colors.card,
            border: `1px solid ${DESIGN.colors.border}`,
            boxShadow: DESIGN.shadows.lg
          }}
        >
          {/* Overview Tab */}
          {activeTab === 'overview' && demoData.myPerformanceOverview && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
                Performance Summary
              </h3>
              <div 
                className="p-5 rounded-xl"
                style={{ backgroundColor: `${accentColor}08` }}
              >
                <p className="text-base leading-relaxed" style={{ color: DESIGN.colors.textPrimary }}>
                  {demoData.myPerformanceOverview.summaryStatement}
                </p>
              </div>
            </div>
          )}
          
          {/* Patterns Tab (replaces Hotspots) */}
          {activeTab === 'patterns' && demoData.myCognitivePatterns && (
            <MyCognitivePatterns data={demoData.myCognitivePatterns} isUniqueIP={isUniqueIP} />
          )}
          {activeTab === 'patterns' && demoData.myOperationalPatterns && (
            <MyCognitivePatterns data={demoData.myOperationalPatterns} isUniqueIP={isUniqueIP} />
          )}
          
          {/* Projects Tab */}
          {activeTab === 'projects' && demoData.myProjects && (
            <div>
              <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
                My Projects
              </h3>
              <MyProjectsView projects={demoData.myProjects} isUniqueIP={isUniqueIP} />
            </div>
          )}
          
          {/* Trends Tab */}
          {activeTab === 'trends' && demoData.myTrends && (
            <div>
              <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
                My Performance Trends
              </h3>
              <PersonalTrends trends={demoData.myTrends} isUniqueIP={isUniqueIP} />
            </div>
          )}
          
          {/* Lane Tab */}
          {activeTab === 'lanes' && demoData.myLane && (
            <div>
              <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
                My Lane Performance
              </h3>
              <MyLaneView lane={demoData.myLane} isUniqueIP={isUniqueIP} />
            </div>
          )}

          {/* Risks Tab - show personal risks */}
          {activeTab === 'risks' && (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: '#10B981' }} />
              <h3 className="text-lg font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
                No Personal Risks
              </h3>
              <p style={{ color: DESIGN.colors.textMuted }}>
                You're on track with your assignments
              </p>
            </div>
          )}
        </div>

        {/* CSS for animations */}
        <style>{`
          @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          .animate-pulse-slow {
            animation: pulse-slow 3s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  // STANDARD VIEW (All other roles)
  return (
    <div className="space-y-8" data-testid="insights-page">
      {/* Demo Role Switcher */}
      <DemoRoleSwitcher 
        activeRole={activeRole} 
        onRoleChange={handleRoleChange}
      />
      
      {/* Scope Label */}
      {demoData?.scope_label && (
        <div className="flex items-center gap-3">
          <span 
            className="text-sm font-bold px-4 py-2 rounded-xl"
            style={{ 
              backgroundColor: isUniqueIP ? UNIQUE_IP_COLORS.primarySubtle : DESIGN.colors.primarySubtle,
              color: isUniqueIP ? UNIQUE_IP_COLORS.primary : DESIGN.colors.primary
            }}
          >
            {demoData.scope_label}
          </span>
        </div>
      )}
      
      {/* Executive Summary Banner - hide for operations and team members */}
      {!demoData?.hideStrategicMetrics && !demoData?.hideOrgMetrics && (
        <ExecutiveSummaryBanner health={displayInsights?.health || insights?.health} />
      )}
      
      {/* Health & Cognitive Load Cards - hide cognitive load for simple views */}
      <div className={`grid grid-cols-1 ${!demoData?.hideStrategicMetrics ? 'lg:grid-cols-2' : ''} gap-6`}>
        <PremiumHealthCard health={displayInsights?.health || insights?.health} />
        {!demoData?.hideStrategicMetrics && (
          <PremiumCognitiveLoadCard cognitive={displayInsights?.cognitive_load || insights?.cognitive_load} />
        )}
      </div>
      
      {/* Premium Tab Navigation */}
      <div className="overflow-x-auto pb-2">
        <PremiumTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
      
      {/* Tab Content */}
      <div 
        className="rounded-2xl p-6"
        style={{ 
          backgroundColor: DESIGN.colors.card,
          border: `1px solid ${DESIGN.colors.border}`,
          boxShadow: DESIGN.shadows.lg
        }}
      >
        {/* Risks Tab */}
        {activeTab === 'risks' && (
          <div className="space-y-4">
            <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
              Risk Indicators
            </h3>
            <div className="space-y-4">
              {mockRisks.map((risk, i) => (
                <PremiumRiskCard key={i} risk={risk} />
              ))}
            </div>
          </div>
        )}
        
        {/* Hotspots Tab */}
        {activeTab === 'hotspots' && (
          <div>
            <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
              Cognitive Load Hotspots
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mockHotspots.map((person, i) => (
                <HotspotCard key={i} person={person} />
              ))}
            </div>
          </div>
        )}
        
        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div>
            <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
              Key Performance Metrics
            </h3>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {mockMetrics.map((metric, i) => (
                <MetricTerminalCard key={i} metric={metric} />
              ))}
            </div>
          </div>
        )}
        
        {/* Lanes Tab */}
        {activeTab === 'lanes' && (
          <div>
            <h3 className="text-lg font-bold mb-6" style={{ color: DESIGN.colors.textPrimary }}>
              Lane Performance Overview
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {mockLanes.map((lane, i) => (
                <LanePerformanceCard key={i} lane={lane} />
              ))}
            </div>
          </div>
        )}
        
        {/* Other tabs placeholder */}
        {['projects', 'trends', 'team', 'reviews'].includes(activeTab) && (
          <div className="text-center py-12">
            <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: DESIGN.colors.textMuted }} />
            <h3 className="text-lg font-bold mb-2" style={{ color: DESIGN.colors.textPrimary }}>
              {tabs.find(t => t.id === activeTab)?.label} Intelligence
            </h3>
            <p style={{ color: DESIGN.colors.textMuted }}>
              Advanced analytics and insights for this section
            </p>
          </div>
        )}
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Insights;
